import { useRef, useEffect, useState, useMemo, PointerEvent } from 'react';
import { prefersReducedMotion } from '../utils/device';
import './CurvedLoop.css';

// Fallback for useId in React 17
let idCounter = 0;
const useInternalId = () => {
  const [id] = useState(() => `id-${idCounter++}`);
  return id;
};

interface CurvedLoopProps {
  marqueeText?: string;
  speed?: number;
  className?: string;
  curveAmount?: number;
  direction?: 'left' | 'right';
  interactive?: boolean;
}

const CurvedLoop = ({
  marqueeText = '',
  speed = 1,
  className,
  curveAmount = 400,
  direction = 'left',
  interactive = true
}: CurvedLoopProps) => {
  // Ensure the text has a nice separator for the loop
  const text = useMemo(() => {
    return marqueeText.trim() + '\u00A0'; // Just one non-breaking space to separate repeats
  }, [marqueeText]);

  const measureRef = useRef<SVGTextElement>(null);
  const textPathRef = useRef<SVGTextPathElement>(null);
  const [spacing, setSpacing] = useState(0);
  const offsetRef = useRef(0);
  const frameRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(true);
  const [isDocVisible, setIsDocVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden
  );
  const reducedMotion = useMemo(() => prefersReducedMotion(), []);

  const uid = useInternalId();
  const pathId = `curve-${uid}`;
  // Centered in 400px height viewBox
  const pathD = `M-100,200 Q500,${200 + curveAmount} 1540,200`;

  const dragRef = useRef(false);
  const lastXRef = useRef(0);
  const dirRef = useRef(direction);
  const velRef = useRef(0);

  // Use a much larger buffer for the repetitions to ensure the path is always saturated
  const totalText = useMemo(() => {
    if (!spacing) return text;
    // We want to cover at least 5000 units of path length to be safe
    const count = Math.ceil(5000 / spacing) + 3;
    return Array(count).fill(text).join('');
  }, [text, spacing]);

  const ready = spacing > 0;

  useEffect(() => {
    if (measureRef.current) {
      setSpacing(measureRef.current.getComputedTextLength());
    }
  }, [text, className]);

  useEffect(() => {
    const remeasure = () => {
      if (measureRef.current) {
        setSpacing(measureRef.current.getComputedTextLength());
      }
    };

    window.addEventListener("resize", remeasure);
    return () => window.removeEventListener("resize", remeasure);
  }, [text, className]);

  useEffect(() => {
    // Webfonts swap in after mount; the first measurement used the fallback font.
    const fonts = typeof document !== 'undefined'
      ? (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts
      : undefined;
    if (!fonts) return;

    let cancelled = false;
    fonts.ready.then(() => {
      if (!cancelled && measureRef.current) {
        setSpacing(measureRef.current.getComputedTextLength());
      }
    });
    return () => {
      cancelled = true;
    };
  }, [text, className]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVisibilityChange = () => setIsDocVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (!spacing) return;
    if (textPathRef.current) {
      // Start at a multiple of spacing to ensure we are aligned with a "start" of a repeat
      const initial = -spacing * 2; 
      textPathRef.current.setAttribute('startOffset', initial + 'px');
      offsetRef.current = initial;
    }
  }, [spacing]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Static render under reduced motion; pause offscreen and in hidden tabs.
    if (!spacing || !ready || !isInView || !isDocVisible || reducedMotion) return;

    const step = () => {
      if (!dragRef.current && textPathRef.current) {
        const delta = dirRef.current === 'right' ? speed : -speed;
        let newOffset = offsetRef.current + delta;

        // Wrap logic: when we've moved by one full spacing, reset
        const wrapPoint = spacing;
        if (newOffset <= -wrapPoint * 2) newOffset += wrapPoint;
        if (newOffset > -wrapPoint) newOffset -= wrapPoint;

        textPathRef.current.setAttribute('startOffset', newOffset + 'px');
        offsetRef.current = newOffset;
      }
      frameRef.current = requestAnimationFrame(step);
    };
    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [spacing, speed, ready, isInView, isDocVisible, reducedMotion]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!interactive) return;
    dragRef.current = true;
    lastXRef.current = e.clientX;
    velRef.current = 0;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!interactive || !dragRef.current || !textPathRef.current) return;
    const dx = e.clientX - lastXRef.current;
    lastXRef.current = e.clientX;
    velRef.current = dx;

    let newOffset = offsetRef.current + dx;

    const wrapPoint = spacing;
    if (newOffset <= -wrapPoint * 2) newOffset += wrapPoint;
    if (newOffset > -wrapPoint) newOffset -= wrapPoint;

    textPathRef.current.setAttribute('startOffset', newOffset + 'px');
    offsetRef.current = newOffset;
  };

  const endDrag = () => {
    if (!interactive) return;
    dragRef.current = false;
    dirRef.current = velRef.current > 0 ? 'right' : 'left';
  };

  const cursorStyle = interactive ? (dragRef.current ? 'grabbing' : 'grab') : 'auto';

  return (
    <div
      ref={containerRef}
      className="curved-loop-jacket"
      style={{ visibility: ready ? 'visible' : 'hidden', cursor: cursorStyle }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
    >
      {/* Decorative marquee: repeated text is noise for assistive tech */}
      <svg className="curved-loop-svg" viewBox="0 0 1440 400" aria-hidden="true">
        <text ref={measureRef} xmlSpace="preserve" style={{ visibility: 'hidden', opacity: 0, pointerEvents: 'none' }}>
          {text}
        </text>
        <defs>
          <path id={pathId} d={pathD} fill="none" stroke="transparent" />
        </defs>
        {ready && (
          <text fontWeight="bold" xmlSpace="preserve" className={className}>
            <textPath ref={textPathRef} href={`#${pathId}`} startOffset="0px" xmlSpace="preserve">
              {totalText}
            </textPath>
          </text>
        )}
      </svg>
    </div>
  );
};

export default CurvedLoop;
